import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  message,
  DatePicker,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getDetail, addUser, updateUser } from '../../api/user';
import dayjs from 'dayjs';
import AvatarUpload from '../../components/Upload';

const BACK_PATH = '/users';

const STATUS_OPTIONS = [
  { value: 1, label: '启用' },
  { value: 0, label: '禁用' },
];
const LOCK_OPTIONS = [
  { value: 0, label: '未锁定' },
  { value: 1, label: '已锁定' },
];
const NEED_CHANGE_PASSWORD_OPTIONS = [
  { value: 0, label: '否' },
  { value: 1, label: '是' },
];

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNew = !id || id === 'new';

  const [detailLoading, setDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [operationType, setOperationType] = useState<'view' | 'edit'>(isNew ? 'edit' : 'view');

  const loadDetail = useCallback(async () => {
    if (!id || isNew) return;
    setDetailLoading(true);
    try {
      const res = (await getDetail({ id: Number(id) })) as {
        res_status_code?: string;
        res_content?: Record<string, unknown>;
        res_message?: string;
      };
      if (res?.res_status_code === '0' && res?.res_content) {
        const data = res.res_content;
        const expiryDate = data.expiryDate ?? data.ExpiryDate;
        form.setFieldsValue({
          loginId: data.loginId ?? data.LoginId ?? '',
          username: data.username ?? data.Username ?? '',
          phone: data.phone ?? data.Phone ?? '',
          email: data.email ?? data.Email ?? '',
          avatar: data.avatar ?? data.Avatar ?? '',
          status: data.status ?? data.Status ?? 1,
          isLock: data.isLock ?? data.IsLock ?? 0,
          needChangePassword: data.needChangePassword ?? data.NeedChangePassword ?? 0,
          source: data.source ?? data.Source ?? '',
          unionId: data.unionId ?? data.UnionId ?? '',
          expiryDate: expiryDate ? dayjs(expiryDate as string) : undefined,
        });
      } else {
        message.error(res?.res_message || '获取详情失败');
      }
    } catch (e) {
      message.error('获取详情失败');
    } finally {
      setDetailLoading(false);
    }
  }, [id, isNew, form]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        status: 1,
        isLock: 0,
        needChangePassword: 0,
      });
    } else {
      loadDetail();
    }
  }, [id, isNew, loadDetail, form]);

  const handleReturn = () => navigate(BACK_PATH);
  const handleEdit = () => setOperationType('edit');

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        loginId: values.loginId,
        username: values.username,
        phone: values.phone ?? '',
        email: values.email ?? '',
        avatar: values.avatar ?? '',
        status: values.status ?? 1,
        isLock: values.isLock ?? 0,
        needChangePassword: values.needChangePassword ?? 0,
        source: values.source ?? '',
        unionId: values.unionId ?? '',
        expiryDate: values.expiryDate ? (values.expiryDate as dayjs.Dayjs).toISOString?.() ?? (values.expiryDate as string) : undefined,
      };
      setSubmitLoading(true);
      if (isNew) {
        const res = (await addUser(payload)) as { res_status_code?: string; res_message?: string };
        if (res?.res_status_code === '0') {
          message.success(res?.res_message || '新增成功');
          navigate(BACK_PATH);
        } else {
          message.error(res?.res_message || '新增失败');
        }
      } else {
        const res = (await updateUser({ id: Number(id), ...payload })) as {
          res_status_code?: string;
          res_message?: string;
        };
        if (res?.res_status_code === '0') {
          message.success(res?.res_message || '保存成功');
          setOperationType('view');
          loadDetail();
        } else {
          message.error(res?.res_message || '保存失败');
        }
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('保存失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const isView = operationType === 'view';
  const isDisabled = isView && !isNew;

  return (
    <div style={{ padding: 16, background: '#fff', minHeight: '100%' }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        {isView && !isNew && (
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            编辑
          </Button>
        )}
        {(!isView || isNew) && (
          <Button type="primary" icon={<SaveOutlined />} loading={submitLoading} onClick={handleSave}>
            保存
          </Button>
        )}
        <Button icon={<ArrowLeftOutlined />} onClick={handleReturn}>
          返回
        </Button>
      </div>

      <Form
        form={form}
        layout="horizontal"
        labelAlign="right"
        colon={false}
        labelCol={{ flex: '140px' }}
        wrapperCol={{ flex: 1 }}
        disabled={isDisabled}
      >
        <Card loading={detailLoading} title="基本信息" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="loginId"
                label="登录ID"
                rules={[{ required: true, message: '请输入登录ID' }]}
              >
                <Input placeholder="登录ID（唯一）" disabled={!isNew && isView} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input placeholder="用户名" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="phone" label="手机号">
                <Input placeholder="手机号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="email" label="邮箱">
                <Input placeholder="邮箱" />
              </Form.Item>
            </Col>
           
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="status" label="状态">
                <Select placeholder="状态" options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="isLock" label="锁定状态">
                <Select placeholder="锁定状态" options={LOCK_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="needChangePassword"
                label="需修改密码"
                tooltip="用户初始密码：Ziggie!23"
              >
                <Select placeholder="是否需要修改密码" options={NEED_CHANGE_PASSWORD_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="source" label="来源">
                <Input placeholder="如 manual、feishu" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="unionId" label="唯一标识">
                <Input placeholder="用于第三方账号关联" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="expiryDate" label="账号过期时间">
                <DatePicker showTime style={{ width: '100%' }} placeholder="选择过期时间" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="avatar" label="头像">
                <AvatarUpload uploadText="上传头像" maxFiles={1} disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </div>
  );
};

export default UserDetail;